// Configurari (Administrator Sistem). Reference: docs/caiet-uc-spec.md UC20-03,
// UC20-04, UC20-05, UC20-10, UC40-06 / UC50-06 / UC51-03, SEC-08, UC15-05,
// SEC-04 / SEC-05, and the bucketing in docs/shape-ops-system-config.md.
//
// Two buckets, no third. A parameter that can be changed at runtime is stored as
// data and edited here; a parameter decided at boot stays an environment
// variable and is rendered read-only with the reason it is not editable here and
// the name of whoever does change it, never as a disabled input (FR-034).
//
// The rule this screen is held to is FR-036: a control it offers must govern the
// behaviour its label describes. So every runtime parameter is an input — but
// only once the owning service can actually keep the value. `editable` is the
// bff's statement about that, per parameter, and it is the single seam between
// this screen and the store the owning services carry (shape §7 stage 2, spec
// 002 T105-T110): while it is false the row is read-only and says why, and the
// day the store lands the same row becomes a field with no change here. Where a
// control exists but is inert for another reason — the stored cron expression is
// not what fires the sync (shape §5) — the screen says so in words as well.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { TableColumn, TableRowData } from '@egov-moldova/mud/loader';
import {
  MudButton,
  MudIcon,
  MudTextInput,
  MudModal,
  MudToast,
  MudSpinner,
  MudSwitch,
  MudTable,
  MudTag,
  MudInfoBox,
} from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { QueryState } from '../../components/QueryState/QueryState';
import { useRoleLabel } from '../../hooks/use-observer-roles';
import {
  formatDateTime,
  onModalDismiss,
  onRowOpen,
  PageHeader,
  RouterLink,
  RowActionsMenu,
  DraftNumericInput,
} from '@simsm/ui-shared';
import { isValidCronExpression } from '@simsm/shared-types';
import { describeSchedule } from './system-config-schedule';
import {
  draftStatus,
  draftedValue,
  isOutOfRange,
  pendingChanges,
  settingRowKind,
  type ScalarSetting,
  type SettingsDraft,
} from './system-config-draft';
import './system-config.css';

interface LastRun {
  startedAtIso: string;
  finishedAtIso: string;
  status: string;
  itemsRead: number;
  itemsChanged: number;
  error: string;
  trigger: string;
}

interface NomenclatorConfig {
  nomenclatorType: string;
  sourceUrl: string;
  cronExpression: string;
  timeoutSeconds: number;
  enabled: boolean;
  timeZone: string;
  nextRunAtIso: string;
  lastFiredAtIso: string;
  updatedAtIso: string;
  updatedBy: string;
  updatedByRole: string;
  lastRun: LastRun | null;
  requiresBasicAuth: boolean;
  /** Already past an unclaimed fire time: the run is the next tick, not `nextRunAtIso`. */
  dueNow: boolean;
}

// `ScalarSetting` is the half the draft rules reason about (key, unit, value,
// bounds, editable, available); the rest is provenance and attribution the
// screen shows and nothing decides on.
interface ServiceSetting extends ScalarSetting {
  ownerService: string;
  envVar: string;
  /** What the row holds, in the screen's unit; null while no row exists. */
  storedValue: number | null;
  /** The stored row sat outside this build's bounds and the service pulled it in. */
  clamped: boolean;
  updatedAtIso: string | null;
  /** IDNP or client_id, never a uuid. */
  updatedBy: string | null;
  updatedByRole: string | null;
}

/** One owning service that did not answer this read or this write. */
interface SettingFailure {
  ownerService: string;
  code: string;
  message: string;
}

interface StartupSetting {
  key: string;
  value: string;
  source: string;
  ownerService: string;
  secret: boolean;
  present: boolean;
}

interface Constraints {
  timeoutSecondsMin: number;
  timeoutSecondsMax: number;
  jobDeadlineSeconds: number;
  tickGranularityMinutes: number;
  // The bound this screen promises for "takes effect without a restart"
  // (FR-031). It is the owning services' setting-cache TTL, and it arrives from
  // the bff so the promise and the mechanism cannot drift (shape §3.3).
  settingPropagationSeconds: number;
}

interface SystemConfig {
  nomenclators: NomenclatorConfig[];
  serviceSettings: ServiceSetting[];
  startupSettings: StartupSetting[];
  constraints: Constraints;
  /** Empty when every owning service answered. */
  failures?: SettingFailure[];
}

interface ScheduleDraft {
  nomenclatorType: string;
  cronExpression: string;
  timeoutSeconds: number;
  enabled: boolean;
  requiresBasicAuth: boolean;
}

const CONFIG_KEY = 'admin-system-config';

// The settings the screen groups under its own heading; anything the bff adds
// later without a section falls into the reporting group rather than vanishing.
const SECTION_OF_SETTING: Record<string, 'codes' | 'reporting' | 'alerts' | 'session' | 'api'> = {
  medicineCodeGraceMonths: 'codes',
  correctionWindowDays: 'reporting',
  // AG11, the window of the days of coverage that alert A5 reports (specs/060-agreed-alerts, C6).
  consumptionWindowDays: 'alerts',
  sessionTtlMinutes: 'session',
  clientTokenTtlSeconds: 'api',
};

// The two lifetimes that bind at the moment they are handed out. Lowering either
// does not shorten a session already in Redis or a token already signed: the
// session's expiry is set as the key's TTL, and UC15-06 requires token
// verification to stay cryptographic with no database hit. The screen says so on
// the row, which is what makes it documented behaviour rather than a surprise an
// administrator discovers by watching nothing happen (shape §3.3).
const BINDS_AT_CREATION = new Set(['sessionTtlMinutes', 'clientTokenTtlSeconds']);

// One extra sentence a row owes its reader, keyed by parameter. There is exactly
// one today: the medicine-code sweep applies two clocks — the months since the
// authorisation expired (UC20-10) and the months since the code last appeared in
// the nomenclator (UC20-09) — and the feed carries no expiry date at all, so for
// a code synchronisation created only the second clock can ever start. Naming
// that on the row is what stops the label from advertising a rule the system
// cannot apply to most of its codes (FR-036, shape §6.2).
const SETTING_NOTE: Record<string, string> = {
  medicineCodeGraceMonths: 'system_config.setting.medicineCodeGraceMonths.feed_gap',
};

// The one place this screen applies the timeout bound, and it applies the API's
// numbers rather than its own. The bound is already declared three times — the
// advertised text, the API contract and the database CHECK
// `timeout_seconds BETWEEN 5 AND 600` — and a fourth copy here is how they drift.
function timeoutInRange(seconds: number, constraints: Constraints): boolean {
  return (
    Number.isInteger(seconds) &&
    seconds >= constraints.timeoutSecondsMin &&
    seconds <= constraints.timeoutSecondsMax
  );
}

export function SystemConfigPage(): JSX.Element {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  // The one role labeller the whole interface reads. A second mapping from role
  // code to copy is the defect this feature spent C2 removing.
  const roleLabel = useRoleLabel();
  const [draft, setDraft] = useState<ScheduleDraft | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // A Tolgee key, never a sentence: the refusal the administrator reads is ours,
  // in their language. Holding the service's own `message` here is what put a
  // bare "CRON_EXPRESSION_REQUIRED" in front of a Romanian reader — 146 of the
  // 252 coded throw sites send no human text at all, so the code *is* the
  // message. `apiErrorKey` maps it; the fallback names the operation.
  const [errorKey, setErrorKey] = useState<string | null>(null);
  // The settings form keeps its own refusal, separate from the schedule modal's.
  // One shared key would print a failed save inside the schedule modal the next
  // time it opens — a message about an act the reader is not performing, which
  // is the same class of defect as a banner that claims more than it knows.
  const [settingsErrorKey, setSettingsErrorKey] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  // Only what the administrator has typed over. Absent means "whatever the
  // service reports", so a value another administrator changes meanwhile is
  // picked up on the next read instead of being masked by a stale copy.
  const [settingsDraft, setSettingsDraft] = useState<SettingsDraft>({});

  const config = useQuery({
    queryKey: [CONFIG_KEY],
    queryFn: async () => {
      // A 502 SYSTEM_CONFIG_PARTIAL is not a failed read: it is everything the
      // bff could read, plus the list of owning services that did not answer
      // (shape §3.4, FR-037). The response interceptor keeps only status, code
      // and message, so letting it reject would throw that body away and blank a
      // page most of which arrived intact. Accepting the status here is what
      // lets the screen show what it knows and say the rest is unknown.
      const response = await apiClient.get<SystemConfig>('/admin/system-config', {
        validateStatus: (status) => (status >= 200 && status < 300) || status === 502,
      });
      if (response.status === 502 && !Array.isArray(response.data?.serviceSettings)) {
        // A 502 from anything but that contract — a proxy, the bff's own crash —
        // carries no configuration at all, so it has to reach QueryState as the
        // failure it is rather than render as an empty screen.
        throw new Error('SYSTEM_CONFIG_UNREADABLE');
      }
      return response.data;
    },
  });

  const data = config.data;
  const nomenclators = data?.nomenclators ?? [];
  const constraints = data?.constraints;
  const serviceSettings = data?.serviceSettings ?? [];
  // Named once, in the order the bff reported them, with duplicates removed: two
  // parameters of the same unreachable service are one outage, not two.
  const failedServices = [...new Set((data?.failures ?? []).map((f) => f.ownerService))];
  // What the footer asks of the draft, computed once per render from the values
  // the service reports — never from a copy of them held in state.
  const changes = pendingChanges(serviceSettings, settingsDraft);

  const saveSchedule = useMutation({
    mutationFn: (next: ScheduleDraft) =>
      apiClient
        .put<NomenclatorConfig>(`/admin/system-config/nomenclators/${next.nomenclatorType}`, {
          cronExpression: next.cronExpression,
          timeoutSeconds: next.timeoutSeconds,
          enabled: next.enabled,
        })
        .then((r) => r.data),
    onSuccess: (saved) => {
      setDraft(null);
      setErrorKey(null);
      void queryClient.invalidateQueries({ queryKey: [CONFIG_KEY] });
      setSuccessMessage(
        t('system_config.schedule.saved', {
          type: t(`system_config.type.${saved.nomenclatorType}`),
        }),
      );
    },
    onError: (err: unknown) => setErrorKey(apiErrorKey(err, 'system_config.schedule.save_failed')),
  });

  // UC20-10 / UC40-06 / SEC-08 / UC15-05 — the four scalars, saved together
  // (shape §3.4). The write fans out to three services and is deliberately not
  // transactional, so a partial failure answers 502 with the effective values in
  // the body. Either way the screen re-reads: what it shows afterwards is what
  // the system holds, never what was typed (FR-037, shape §8 risk 4).
  const saveSettings = useMutation({
    // The propagation bound travels with the request rather than being read out
    // of a closure at completion time: the success banner quotes it, and quoting
    // a number that was not on screen when the administrator pressed Save is how
    // a message stops describing what happened.
    mutationFn: async ({ payload }: { payload: Record<string, number>; seconds: number }) => {
      const response = await apiClient.put<SystemConfig>('/admin/system-config', payload, {
        // Same contract as the read: a 502 here means some legs landed and some
        // did not, and the body still carries the effective value of every
        // parameter. That is a result to render, not an error to swallow.
        validateStatus: (status) => (status >= 200 && status < 300) || status === 502,
      });
      if (response.status === 502 && !Array.isArray(response.data?.serviceSettings)) {
        throw new Error('SYSTEM_CONFIG_UNREADABLE');
      }
      return response;
    },
    onSuccess: (response, variables) => {
      // Either way the draft stops describing anything: on a full success it is
      // saved, and on a partial one some legs landed, so what the administrator
      // typed is no longer a truthful description of the pending change. Drop it
      // and re-read; what the screen shows next is what the system holds.
      setSettingsDraft({});
      void queryClient.invalidateQueries({ queryKey: [CONFIG_KEY] });
      // A 502 answers two different events, and telling them apart is the whole
      // difference between a true sentence and a false one. The endpoint re-reads
      // EVERY owning service after the write, so a service that has nothing to do
      // with this change and happens to be down puts a failure in the body —
      // reporting that as "some settings were not saved" would claim a write
      // failed that in fact landed. Only a `SETTING_WRITE_FAILED` is a write that
      // did not happen; a read failure is answered by the banner the re-read
      // raises, which names the services that did not answer.
      const writeFailed = (response.data.failures ?? []).some(
        (failure) => failure.code === 'SETTING_WRITE_FAILED',
      );
      if (writeFailed) {
        setSettingsErrorKey('system_config.settings.save_partial');
        setSuccessMessage(null);
        return;
      }
      setSettingsErrorKey(null);
      setSuccessMessage(t('system_config.settings.saved', { seconds: variables.seconds }));
    },
    // A refusal — a value out of range, a lapsed session, a request that never
    // arrived — changed nothing, so the administrator keeps what they typed and
    // the screen names the refusal rather than claiming a partial save.
    onError: (err: unknown) =>
      setSettingsErrorKey(apiErrorKey(err, 'system_config.settings.save_failed')),
  });

  const runSync = useMutation({
    mutationFn: (type: string) =>
      apiClient
        .post<{
          created: number;
          updated: number;
          errors: number;
        }>(`/admin/system-config/nomenclators/${type}/sync`, {})
        .then((r) => ({ type, ...r.data })),
    onSuccess: (result) => {
      setSyncing(null);
      setErrorKey(null);
      void queryClient.invalidateQueries({ queryKey: [CONFIG_KEY] });
      setSuccessMessage(
        t('system_config.sync.done', {
          type: t(`system_config.type.${result.type}`),
          created: result.created,
          updated: result.updated,
          errors: result.errors,
        }),
      );
    },
    onError: (err: unknown) => {
      setSyncing(null);
      setErrorKey(apiErrorKey(err, 'system_config.sync.failed'));
    },
  });

  function readableSchedule(expression: string): string {
    const described = describeSchedule(expression);
    switch (described.kind) {
      case 'daily':
        return t('system_config.schedule.daily', { time: described.time });
      case 'weekly':
        return t('system_config.schedule.weekly', {
          day: t(`system_config.day.${described.day}`),
          time: described.time,
        });
      case 'everyHours':
        return t('system_config.schedule.every_hours', { hours: described.hours });
      case 'everyMinutes':
        return t('system_config.schedule.every_minutes', { minutes: described.minutes });
      default:
        return t('system_config.schedule.custom');
    }
  }

  // FR-034 / shape §4 — five rows, three reasons. The reason follows from what
  // the row is, so it is derived rather than tabulated: a secret is rotated by
  // infrastructure, an address travels with the NetworkPolicy egress rules that
  // permit the call, and anything else on this list is a platform value Helm
  // owns. A key the bff adds later inherits the platform sentence, which is the
  // true one for a Helm value and never claims more than it knows.
  function startupReason(setting: StartupSetting): string {
    if (setting.secret) return t('system_config.startup.reason.secret');
    if (setting.key.endsWith('_URL')) return t('system_config.startup.reason.address');
    return t('system_config.startup.reason.platform');
  }

  function lastRunLine(row: NomenclatorConfig): string {
    if (!row.lastRun) return t('system_config.last_run.never');
    if (row.lastRun.status === 'FAILED') {
      return row.lastRun.error || t('system_config.last_run.failed');
    }
    return t('system_config.last_run.summary', {
      read: row.lastRun.itemsRead,
      changed: row.lastRun.itemsChanged,
    });
  }

  const columns: TableColumn[] = [
    { key: 'actions', label: '', width: '56px' },
    { key: 'nomenclator', label: t('system_config.column.nomenclator'), width: '30%' },
    { key: 'schedule', label: t('system_config.column.schedule') },
    { key: 'sync', label: t('system_config.column.sync') },
  ];

  const rows: TableRowData[] = nomenclators.map((row) => ({
    id: row.nomenclatorType,
    actions: '',
    nomenclator: '',
    schedule: '',
    sync: '',
  }));

  function settingsOf(
    section: 'codes' | 'reporting' | 'alerts' | 'session' | 'api',
  ): ServiceSetting[] {
    return serviceSettings.filter(
      (setting) => (SECTION_OF_SETTING[setting.key] ?? 'reporting') === section,
    );
  }

  // `settingRowKind` decides which of three rows this parameter gets, and none of
  // them is ever a disabled input — a disabled control reads as "temporarily
  // unavailable", which is a different statement from all three of these:
  //
  //  - `editable` — a field carrying the owning service's own bounds and the
  //    sentence saying how long the change takes to reach every replica. No tag:
  //    FR-034 reserves the neutral "system configuration" tag for a parameter
  //    that genuinely cannot be edited here, and wearing it on an editable row
  //    would deny what the row plainly does.
  //  - `unreadable` — the owning service did not answer, so the value is not
  //    known and nothing stands in for it.
  //  - `read-only` — a runtime parameter (FR-030, the owner's ruling of
  //    2026-08-31) whose value SIMSM does not keep yet. A field would be a
  //    control that saves nothing (FR-036).
  function renderSetting(setting: ServiceSetting): JSX.Element {
    const unit = t(`system_config.unit.${setting.unit}`);
    const label = t(`system_config.setting.${setting.key}.label`);
    // Bound before use so the index access narrows: the map holds a key for one
    // parameter, not for all four.
    const note = SETTING_NOTE[setting.key];
    const value = draftedValue(setting, settingsDraft);
    const kind = settingRowKind(setting);
    const outOfRange = value !== null && isOutOfRange(setting, value);
    return (
      <div className="simsm-sysconfig__row" key={setting.key}>
        <span className="simsm-sysconfig__row-label">
          {label}
          <span className="simsm-sysconfig__row-hint">
            {t(`system_config.setting.${setting.key}.hint`)}
          </span>
          {note ? <span className="simsm-sysconfig__row-hint">{t(note)}</span> : null}
          {/* The line that used to stand here read "Aplicat de catalog-service ·
              variabila MEDICINE_CODE_GRACE_MONTHS" and it is gone, per
              specs/039-admin-screens §A-25. An Administrator Sistem cannot act on
              a deployment's service name or on an environment variable's name:
              the row already says what the parameter governs, what values it
              takes, who last changed it and when the change takes effect, and
              that is the whole of the governance the caiet asks for. The names
              still exist on `setting` — the startup section below is about
              exactly those, and that is where they belong. */}
          {kind === 'unreadable' ? (
            <span className="simsm-sysconfig__row-hint">
              {t('system_config.setting.unavailable', { service: setting.ownerService })}
            </span>
          ) : null}
          {setting.clamped && setting.storedValue !== null && setting.value !== null ? (
            // The stored row is outside this build's bounds and the service
            // pulled it in, so the number an administrator set is not the number
            // in force. Saying which is which is the whole point (shape §8 risk 3).
            <span className="simsm-sysconfig__row-hint">
              {t('system_config.setting.clamped', {
                stored: setting.storedValue,
                value: setting.value,
                unit,
              })}
            </span>
          ) : null}
          {kind === 'editable' ? (
            <>
              {constraints ? (
                <span className="simsm-sysconfig__row-hint">
                  {t('system_config.setting.effect', {
                    seconds: constraints.settingPropagationSeconds,
                  })}
                </span>
              ) : null}
              {BINDS_AT_CREATION.has(setting.key) ? (
                <span className="simsm-sysconfig__row-hint">
                  {t('system_config.setting.binds_at_creation')}
                </span>
              ) : null}
            </>
          ) : null}
          {kind === 'read-only' ? (
            <>
              <span className="simsm-sysconfig__row-hint">
                {t('system_config.setting.bounds', { min: setting.min, max: setting.max, unit })}
              </span>
              <span className="simsm-sysconfig__row-hint">
                {t('system_config.setting.not_stored')}
              </span>
            </>
          ) : null}
          {kind === 'unreadable' ? null : (
            <span className="simsm-sysconfig__row-hint">
              {setting.storedValue === null || !setting.updatedBy
                ? t('system_config.setting.from_default')
                : t('system_config.setting.changed_by', {
                    actor: setting.updatedBy,
                    role: roleLabel(setting.updatedByRole ?? ''),
                    moment: formatDateTime(setting.updatedAtIso),
                  })}
            </span>
          )}
        </span>
        <span className="simsm-sysconfig__row-value">
          {kind === 'editable' && value !== null ? (
            // `mudChange` carries what mud-numeric-input has clamped to
            // [min, max] on blur; `mudInput` carries what was typed. Reading the
            // second is what let a field read 5 while the draft held 1 and the
            // request carried 1 (audit 2026-08-30 ST3), so this reads the first
            // only — and the draft is checked against the same bounds anyway, so
            // an out-of-range value disables Save even if the clamp ever fails.
            <span className="simsm-sysconfig__row-field">
              <DraftNumericInput
                size="md"
                aria-label={label}
                ariaValuetext={`${value} ${unit}`}
                value={value}
                min={setting.min}
                max={setting.max}
                invalid={outOfRange}
                errorText={t('system_config.setting.out_of_range', {
                  min: setting.min,
                  max: setting.max,
                  unit,
                })}
                helperText={t('system_config.setting.bounds', {
                  min: setting.min,
                  max: setting.max,
                  unit,
                })}
                onMudChange={(e: CustomEvent<{ value: number | null }>) =>
                  setSettingsDraft({
                    ...settingsDraft,
                    // An emptied field is not "unchanged": restoring the stored
                    // number silently would undo an edit in progress, so the
                    // floor stands in and the clamp the component already applies
                    // is what the administrator sees.
                    [setting.key]: e.detail.value ?? setting.min,
                  })
                }
              >
                <span slot="suffix">{unit}</span>
              </DraftNumericInput>
            </span>
          ) : kind === 'unreadable' ? (
            <>
              <span>{t('system_config.setting.unknown_value')}</span>
              <MudTag
                variant="status"
                semantic="neutral"
                size="sm"
                label={t('system_config.tag.unavailable')}
              />
            </>
          ) : (
            <>
              <span>
                {setting.value} {unit}
              </span>
              <MudTag
                variant="status"
                semantic="neutral"
                size="sm"
                label={t('system_config.tag.no_store')}
              />
            </>
          )}
        </span>
      </div>
    );
  }
  return (
    <section className="simsm-page simsm-sysconfig">
      <PageHeader title={t('system_config.title')} lede={t('system_config.lede')} />

      {/* `closable` is load-bearing, not decoration. MUD's mud-toast defaults it to
          true, but every other toast in the app opts out with `closable={false}` (they have
          no handler), so this one states it. Under AGE, which defaulted it to false, both
          handlers below were unreachable dead code (audit 2026-08-30). They have to be reachable:
          dismissing is the only thing that ever clears `successMessage` — no mutation, no
          navigation and no query invalidation sets it back to null, so without a close
          button the banner outlives the action it reports. `closeLabel` goes through Tolgee
          because the atom defaults it to its own Romanian word. Both banners sit in the page
          body, outside every modal, so the `mudClose` they now emit — bubbling and composed
          like every other AGE close event — has no MudModal ancestor to take down with it. */}
      {successMessage ? (
        <div className="simsm-inline-outcome">
          <MudToast
            variant="success"
            closable
            closeLabel={t('common.close')}
            onMudClose={() => setSuccessMessage(null)}
          >
            {successMessage}
          </MudToast>
        </div>
      ) : null}
      {errorKey && draft === null ? (
        <MudInfoBox
          emphasis="strong"
          role="alert"
          variant="error"
          closable
          closeLabel={t('common.close')}
          onMudClose={() => setErrorKey(null)}
        >
          {t(errorKey)}
        </MudInfoBox>
      ) : null}
      {settingsErrorKey ? (
        <MudInfoBox
          emphasis="strong"
          role="alert"
          variant="error"
          closable
          closeLabel={t('common.close')}
          onMudClose={() => setSettingsErrorKey(null)}
        >
          {t(settingsErrorKey)}
        </MudInfoBox>
      ) : null}
      {/* A read that only partly succeeded. It is not closable: it describes the
          state of the page below it, and dismissing it would leave rows reading
          "unknown" with nothing on screen saying why. It disappears when the
          services answer again. */}
      {failedServices.length > 0 ? (
        <MudInfoBox emphasis="strong" role="alert" variant="warning">
          {t('system_config.failures.read', { services: failedServices.join(', ') })}
        </MudInfoBox>
      ) : null}

      {/* No empty state, and that is the honest answer rather than a missing one.
          Every row below is declared in source, none is fetched as a collection:
          the bff answers two nomenclator types (`NomenclatorScheduleService
          .findAll` maps NOMENCLATOR_TYPES and substitutes a default row for
          anything unstored), four service settings (`SERVICE_SETTINGS.map`, with
          `unavailableView` standing in for a service that did not answer) and
          eight startup declarations (an explicit allow-list, caiet SEC-04 /
          SEC-05). Fourteen rows, always. A service being down subtracts none of
          them — it produces a row reading "unavailable" plus the warning above,
          which is what this screen shows instead of a blank one. So there is no
          emptiness to condition on, and the `isEmpty={false}` that used to stand
          here was worse than nothing: a branch that could never fire, drawn in
          one of the four shapes decision D3 abolished, reading to every later
          reader as a case somebody had already handled. */}
      <QueryState query={config}>
        <section className="simsm-sysconfig__section simsm-sysconfig__section--wide">
          <div className="simsm-sysconfig__section-head">
            <h2 className="simsm-sysconfig__section-title">
              {t('system_config.nomenclators.title')}
            </h2>
            <p className="simsm-sysconfig__section-lede">{t('system_config.nomenclators.lede')}</p>
          </div>
          <div className="simsm-sysconfig__table">
            <MudTable
              className="simsm-table"
              columns={columns}
              rows={rows}
              rowStyle="zebra"
              disableSort={true}
              aria-label={t('system_config.nomenclators.table_aria')}
              // The whole row opens the schedule's editor; syncing stays in the
              // menu, since a click that starts a sync is not one to make by accident.
              hoverable
              onMudRowClick={onRowOpen((index) => {
                const row = nomenclators[index];
                if (!row) return;
                setDraft({
                  nomenclatorType: row.nomenclatorType,
                  cronExpression: row.cronExpression,
                  timeoutSeconds: row.timeoutSeconds,
                  enabled: row.enabled,
                  requiresBasicAuth: row.requiresBasicAuth,
                });
              })}
            >
              {nomenclators.map((row, index) => (
                <span key={`actions-${row.nomenclatorType}`} slot={`cell-actions-${index}`}>
                  <RowActionsMenu
                    label={t('system_config.action.menu', {
                      type: t(`system_config.type.${row.nomenclatorType}`),
                    })}
                    actions={[
                      {
                        label: t('system_config.action.sync'),
                        onSelect: () => {
                          setSyncing(row.nomenclatorType);
                          runSync.mutate(row.nomenclatorType);
                        },
                        disabled: syncing !== null,
                      },
                      {
                        label: t('system_config.action.edit_schedule'),
                        onSelect: () =>
                          setDraft({
                            nomenclatorType: row.nomenclatorType,
                            cronExpression: row.cronExpression,
                            timeoutSeconds: row.timeoutSeconds,
                            enabled: row.enabled,
                            requiresBasicAuth: row.requiresBasicAuth,
                          }),
                      },
                    ]}
                  />
                </span>
              ))}
              {nomenclators.map((row, index) => (
                <span key={`name-${row.nomenclatorType}`} slot={`cell-nomenclator-${index}`}>
                  <span className="simsm-sysconfig__cell-name">
                    <strong>{t(`system_config.type.${row.nomenclatorType}`)}</strong>
                    <span className="simsm-sysconfig__cell-meta" title={row.sourceUrl}>
                      {t('system_config.source', { url: row.sourceUrl })}
                    </span>
                  </span>
                </span>
              ))}
              {nomenclators.map((row, index) => (
                <span key={`schedule-${row.nomenclatorType}`} slot={`cell-schedule-${index}`}>
                  <span className="simsm-sysconfig__schedule">
                    <span>
                      {row.enabled
                        ? readableSchedule(row.cronExpression)
                        : t('system_config.schedule.disabled')}
                    </span>
                    <dl className="simsm-sysconfig__schedule-details">
                      <div className="simsm-sysconfig__schedule-detail">
                        <dt>{t('system_config.schedule.cron')}</dt>
                        <dd>
                          <code className="simsm-sysconfig__cron">{row.cronExpression}</code>
                        </dd>
                      </div>
                      <div className="simsm-sysconfig__schedule-detail">
                        <dt>{t('system_config.schedule.timeout')}</dt>
                        <dd>
                          {row.timeoutSeconds} {t('system_config.unit.seconds')}
                        </dd>
                      </div>
                    </dl>
                  </span>
                </span>
              ))}
              {nomenclators.map((row, index) => (
                <span key={`sync-${row.nomenclatorType}`} slot={`cell-sync-${index}`}>
                  {syncing === row.nomenclatorType ? (
                    <span className="simsm-sysconfig__running" role="status">
                      <MudSpinner size="sm" label={t('common.loading')} />
                      {t('system_config.sync.running')}
                    </span>
                  ) : (
                    <span className="simsm-sysconfig__schedule">
                      <span>
                        {row.lastRun
                          ? formatDateTime(row.lastRun.startedAtIso)
                          : t('system_config.last_run.never')}
                      </span>
                      <dl className="simsm-sysconfig__schedule-details">
                        <div className="simsm-sysconfig__schedule-detail">
                          <dt>{t('system_config.last_run.result')}</dt>
                          <dd>{lastRunLine(row)}</dd>
                        </div>
                        <div className="simsm-sysconfig__schedule-detail">
                          <dt>{t('system_config.last_run.next')}</dt>
                          {/* Three states, and the screen never claims more than
                              the system knows (T118). Disabled: no next run at
                              all. Due: the moment is behind us and the run is the
                              next tick, whose phase this process cannot compute —
                              so it says "at the next check", not a time. Neither:
                              the moment the stored expression names, carrying the
                              tick tolerance because the run lands after it, never
                              before. */}
                          <dd>
                            {!row.enabled
                              ? t('system_config.schedule.disabled')
                              : row.dueNow
                                ? t('system_config.last_run.next_due', {
                                    minutes: constraints?.tickGranularityMinutes ?? 0,
                                  })
                                : row.nextRunAtIso
                                  ? `${formatDateTime(row.nextRunAtIso)} · ${t(
                                      'system_config.last_run.next_tolerance',
                                      { minutes: constraints?.tickGranularityMinutes ?? 0 },
                                    )}`
                                  : t('system_config.last_run.next_unknown')}
                          </dd>
                        </div>
                      </dl>
                    </span>
                  )}
                </span>
              ))}
            </MudTable>
          </div>
          {/* T115's warning that the stored expression governed nothing is gone,
              because it no longer does: `nomenclator-tick` (infra/helm/simsm/
              values.yaml) runs `--task=nomenclator-sync --due-only` every 15
              minutes and the stored row decides which sources run. What replaces
              it is the tolerance that costs — the schedule is honoured to within
              one tick, never to the minute (owner ruling of 2026-08-31, UC20-03).
              It is a statement of how the system behaves, not a warning, so it is
              a note and not a notification. */}
          {constraints ? (
            <p className="simsm-sysconfig__note">
              {t('system_config.nomenclators.quantisation', {
                minutes: constraints.tickGranularityMinutes,
                zone: nomenclators[0]?.timeZone ?? '',
              })}
            </p>
          ) : null}
        </section>

        <section className="simsm-sysconfig__section">
          <div className="simsm-sysconfig__section-head">
            <h2 className="simsm-sysconfig__section-title">{t('system_config.codes.title')}</h2>
            <p className="simsm-sysconfig__section-lede">{t('system_config.codes.lede')}</p>
          </div>
          <div className="simsm-sysconfig__rows">{settingsOf('codes').map(renderSetting)}</div>
        </section>

        <section className="simsm-sysconfig__section">
          <div className="simsm-sysconfig__section-head">
            <h2 className="simsm-sysconfig__section-title">{t('system_config.reporting.title')}</h2>
            <p className="simsm-sysconfig__section-lede">{t('system_config.reporting.lede')}</p>
          </div>
          <div className="simsm-sysconfig__rows">{settingsOf('reporting').map(renderSetting)}</div>
        </section>

        <section className="simsm-sysconfig__section">
          <div className="simsm-sysconfig__section-head">
            <h2 className="simsm-sysconfig__section-title">{t('system_config.alerts.title')}</h2>
            <p className="simsm-sysconfig__section-lede">{t('system_config.alerts.lede')}</p>
          </div>
          <div className="simsm-sysconfig__rows">{settingsOf('alerts').map(renderSetting)}</div>
        </section>

        <section className="simsm-sysconfig__section">
          <div className="simsm-sysconfig__section-head">
            <h2 className="simsm-sysconfig__section-title">{t('system_config.session.title')}</h2>
            <p className="simsm-sysconfig__section-lede">{t('system_config.session.lede')}</p>
          </div>
          <div className="simsm-sysconfig__rows">{settingsOf('session').map(renderSetting)}</div>
        </section>

        <section className="simsm-sysconfig__section">
          <div className="simsm-sysconfig__section-head">
            <h2 className="simsm-sysconfig__section-title">{t('system_config.api.title')}</h2>
            <p className="simsm-sysconfig__section-lede">{t('system_config.api.lede')}</p>
          </div>
          <div className="simsm-sysconfig__rows">
            {settingsOf('api').map(renderSetting)}
            {/* Two subjects used to share one row called "MConnect credentials"
                (shape §6.2's sibling, §6.4): the secret SIMSM presents when it
                calls MConnect, which is startup configuration, and the credential
                MConnect presents when it calls SIMSM, which is UI-managed data
                (UC70-02). This section is the inbound one, so this row is the
                inbound subject only and it leads to the register that owns it.
                The outbound secret is a startup row further down, with the reason
                it is not editable here and no value at all. It carries no startup
                tag: FR-034 reserves that tag for a parameter that genuinely
                cannot be changed from the interface, and this one can — on its
                own page. */}
            <div className="simsm-sysconfig__row">
              <span className="simsm-sysconfig__row-label">
                {t('system_config.mconnect.inbound_label')}
                <span className="simsm-sysconfig__row-hint">
                  {t('system_config.mconnect.inbound_hint')}
                </span>
              </span>
              <span className="simsm-sysconfig__row-value">
                <RouterLink to="/system-admin/mconnect-credentials" size="sm">
                  {t('system_config.mconnect.inbound_action')}
                </RouterLink>
              </span>
            </div>
          </div>
        </section>

        <section className="simsm-sysconfig__section">
          <div className="simsm-sysconfig__section-head">
            <h2 className="simsm-sysconfig__section-title">{t('system_config.startup.title')}</h2>
            <p className="simsm-sysconfig__section-lede">{t('system_config.startup.lede')}</p>
          </div>
          <div className="simsm-sysconfig__startup">
            {(data?.startupSettings ?? []).map((setting) => (
              <div className="simsm-sysconfig__startup-row" key={setting.key}>
                <span className="simsm-sysconfig__startup-key">
                  <code>{setting.key}</code>
                  <span className="simsm-sysconfig__row-hint">
                    {t('system_config.startup.provenance', {
                      service: setting.ownerService,
                      source: setting.source,
                    })}
                  </span>
                  <span className="simsm-sysconfig__row-hint">{startupReason(setting)}</span>
                </span>
                <span className="simsm-sysconfig__row-value">
                  {/* A secret shows no characters at all, not even masked: a mask
                      still discloses the length (FR-034). The tag says whether it
                      is set, which is the only thing an administrator needs. */}
                  {setting.secret ? (
                    <MudTag
                      variant="status"
                      semantic={setting.present ? 'success' : 'neutral'}
                      label={
                        setting.present
                          ? t('system_config.startup.secret_set')
                          : t('system_config.startup.secret_unset')
                      }
                    />
                  ) : (
                    <>
                      <span>{setting.value || t('system_config.startup.empty')}</span>
                      <MudTag
                        variant="status"
                        semantic="neutral"
                        size="sm"
                        label={t('system_config.tag.startup')}
                      />
                    </>
                  )}
                </span>
              </div>
            ))}
            {/* The two platform values the schedule above is read against: the
                time zone is what a cron expression means, and the run ceiling is
                the bound the per-request timeout has to stay under (shape §4). */}
            {nomenclators[0] ? (
              <div className="simsm-sysconfig__startup-row">
                <span className="simsm-sysconfig__startup-key">
                  <code>cronJobs.timeZone</code>
                  <span className="simsm-sysconfig__row-hint">
                    {t('system_config.startup.timezone_hint')}
                  </span>
                  <span className="simsm-sysconfig__row-hint">
                    {t('system_config.startup.reason.platform')}
                  </span>
                </span>
                <span className="simsm-sysconfig__row-value">
                  <span>{nomenclators[0].timeZone}</span>
                  <MudTag
                    variant="status"
                    semantic="neutral"
                    size="sm"
                    label={t('system_config.tag.startup')}
                  />
                </span>
              </div>
            ) : null}
            {constraints ? (
              <div className="simsm-sysconfig__startup-row">
                <span className="simsm-sysconfig__startup-key">
                  <code>cronJobs.activeDeadlineSeconds</code>
                  <span className="simsm-sysconfig__row-hint">
                    {t('system_config.startup.deadline_hint')}
                  </span>
                  <span className="simsm-sysconfig__row-hint">
                    {t('system_config.startup.reason.platform')}
                  </span>
                </span>
                <span className="simsm-sysconfig__row-value">
                  <span>
                    {constraints.jobDeadlineSeconds} {t('system_config.unit.seconds')}
                  </span>
                  <MudTag
                    variant="status"
                    semantic="neutral"
                    size="sm"
                    label={t('system_config.tag.startup')}
                  />
                </span>
              </div>
            ) : null}
          </div>
        </section>

        {/* The wireframe's sticky save bar (AdminConfigurari.tsx:430-441): the
            actions sit in one place at the end of the form and stay in view on a
            long page, with the state of the changes written beside them.
            It appears only when at least one parameter on this screen is
            actually editable — a save bar over a page of read-only rows would be
            the same lie as an input that saves nothing. The schedule and the
            timeout keep their own Apply inside the modal that edits them; this
            bar is labelled for the settings it governs and claims nothing about
            the rest of the page. */}
        {constraints && serviceSettings.some((s) => settingRowKind(s) === 'editable') ? (
          <div
            className="simsm-sysconfig__savebar"
            role="group"
            aria-label={t('system_config.footer.aria')}
          >
            <span className="simsm-sysconfig__savebar-state">
              {t(`system_config.footer.${draftStatus(changes)}`)}
            </span>
            <MudButton
              shape="circular"
              variant="neutral"
              size="md"
              disabled={!changes.dirty || saveSettings.isPending}
              onClick={() => {
                setSettingsDraft({});
                setSettingsErrorKey(null);
              }}
            >
              {t('system_config.action.discard')}
            </MudButton>
            <MudButton
              shape="circular"
              variant="primary"
              size="md"
              disabled={!changes.dirty || changes.invalid || saveSettings.isPending}
              onClick={() =>
                saveSettings.mutate({
                  payload: changes.payload,
                  seconds: constraints.settingPropagationSeconds,
                })
              }
            >
              {/* `check` is not an icon AGE ships: `icons.manifest.json` has
                  `checkmark-small` / `checkmark-large` and no `check`, so
                  mud-icon logged "Icon not found" and rendered nothing every
                  time this page painted (vendor/age-design/SOURCE_COMMIT
                  7322a157). */}
              <MudIcon slot="icon-start" name="checkmark-small" size={16} />
              {t('system_config.action.save')}
            </MudButton>
          </div>
        ) : null}
      </QueryState>

      <MudModal
        closeLabel={t('common.close')}
        open={draft !== null}
        size="md"
        titleText={
          draft
            ? t('system_config.schedule.modal_title', {
                type: t(`system_config.type.${draft.nomenclatorType}`),
              })
            : ''
        }
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
        // components emit `mudClose` bubbling+composed, so a descendant closing inside this
        // modal reaches this handler too; the guard answers only a real dismissal. Nothing in
        // this form emits one *today* — the error notification below is deliberately not
        // `closable`, and there is no select, tooltip, banner, info-box or phone input here —
        // so do not read the guard as dead weight: it is what keeps adding either a select or
        // a close button to that notification from silently discarding the draft mid-edit.
        onMudClose={onModalDismiss(() => {
          setDraft(null);
          setErrorKey(null);
        })}
      >
        {draft && constraints ? (
          <div className="simsm-sysconfig__form">
            {errorKey ? (
              <MudInfoBox emphasis="strong" role="alert" variant="error">
                {t(errorKey)}
              </MudInfoBox>
            ) : null}
            <MudTextInput
              className="simsm-sysconfig__cron-field"
              label={t('system_config.schedule.cron_label')}
              required
              value={draft.cronExpression}
              placeholder="0 6 * * *"
              invalid={!isValidCronExpression(draft.cronExpression)}
              helperText={
                isValidCronExpression(draft.cronExpression)
                  ? t('system_config.schedule.reads_as', {
                      description: readableSchedule(draft.cronExpression),
                    })
                  : t('system_config.schedule.cron_hint')
              }
              errorText={t('system_config.schedule.cron_invalid')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setDraft({ ...draft, cronExpression: e.detail.value })
              }
            />
            {/* The tolerance belongs where the time is chosen, not only in the
                note behind this modal: an administrator typing 06:00 is deciding
                on the strength of what the field tells them (T118, shape §5.3). */}
            <p className="simsm-sysconfig__note">
              {t('system_config.schedule.tolerance_note', {
                minutes: constraints.tickGranularityMinutes,
              })}
            </p>
            {/* `mudChange` carries the value mud-numeric-input has clamped on
                blur; `mudInput` carries what was typed. Subscribing to the
                second is what let the field read 5 while the draft held 1 and
                Apply sent 1 (audit 2026-08-30 ST3). The bounds are the API's own
                — the advertised text, the clamp and the Apply guard all read
                these two numbers, so the screen cannot drift from the CHECK
                constraint the way a local copy would. */}
            <DraftNumericInput
              label={t('system_config.schedule.timeout_label')}
              required
              value={draft.timeoutSeconds}
              min={constraints.timeoutSecondsMin}
              max={constraints.timeoutSecondsMax}
              invalid={!timeoutInRange(draft.timeoutSeconds, constraints)}
              errorText={t('system_config.schedule.timeout_invalid', {
                min: constraints.timeoutSecondsMin,
                max: constraints.timeoutSecondsMax,
              })}
              helperText={t('system_config.schedule.timeout_hint', {
                min: constraints.timeoutSecondsMin,
                max: constraints.timeoutSecondsMax,
                deadline: constraints.jobDeadlineSeconds,
              })}
              onMudChange={(e: CustomEvent<{ value: number | null }>) =>
                setDraft({ ...draft, timeoutSeconds: e.detail.value ?? 0 })
              }
            >
              <span slot="suffix">{t('system_config.unit.seconds')}</span>
            </DraftNumericInput>
            <p className="simsm-sysconfig__note">{t('system_config.schedule.timeout_effect')}</p>
            <MudSwitch
              checked={draft.enabled}
              onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                setDraft({ ...draft, enabled: e.detail.checked })
              }
            >
              <span slot="label">{t('system_config.schedule.enabled_label')}</span>
            </MudSwitch>
            {draft.requiresBasicAuth ? (
              <p className="simsm-sysconfig__note">
                <MudTag
                  variant="status"
                  semantic="neutral"
                  size="sm"
                  label={t('system_config.tag.startup')}
                />
                {t('system_config.schedule.basic_auth_note')}
              </p>
            ) : null}
          </div>
        ) : null}
        <div slot="actions" className="simsm-sysconfig__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => {
              setDraft(null);
              setErrorKey(null);
            }}
          >
            {t('system_config.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={
              draft === null ||
              constraints === undefined ||
              !isValidCronExpression(draft.cronExpression) ||
              !timeoutInRange(draft.timeoutSeconds, constraints) ||
              saveSchedule.isPending
            }
            onClick={() => {
              if (draft) saveSchedule.mutate(draft);
            }}
          >
            <MudIcon slot="icon-start" name="checkmark-small" size={16} />
            {t('system_config.action.apply_schedule')}
          </MudButton>
        </div>
      </MudModal>
    </section>
  );
}
